'use client';


import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { loginUser } from '../../../services/authService.ts';
import { useLanguage } from '../../../context/LanguageContext';



export default function LoginStep2Credentials({
  userType,
  onBack,
  onSelectUserType
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null); // Changed to null/object for rich errors
  const [loading, setLoading] = useState(false);
  const { translate, language } = useLanguage();

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError({ message: translate("auth.login.fillAllFields") });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError({ message: translate("auth.login.validEmail") });
      return;
    }

    setLoading(true);
    try {
      const credentials = {
        email,
        password,
        userType,
      };

      const response = await loginUser(credentials);

      if (response && response.token) {
        const userData = response.user;
        // ✅ NEW CHECK ADDED HERE
if (userData?.isSuperAdmin) {
  setError({
    message: translate("auth.login.loginrestrict")
  });
  return;
}
        let actualUserType = null;
        if (userData?.buyer && userData.buyer !== null) {
          actualUserType = 'buyer';
        } else if (userData?.seller && userData.seller !== null) {
          actualUserType = 'seller';
        }

        if (actualUserType !== userType) {
          setError({
            message: translate("auth.login.mismatchNotRegistered")
              .replace("{userType}", translate(`auth.login.${userType}Title`).toLowerCase())
              .replace("{actualType}", translate(`auth.login.${actualUserType}Title`).toLowerCase()),
            linkText: translate("auth.login.mismatchClickHere"),
            actualType: actualUserType
          });
          return;
        }

        login(response.token, response.user);
        navigate('/dashboard');
      } else {
        setError({ message: 'Login failed: No token received' });
      }

    } catch (err) {
      // try to grab server-sent message first
      const serverData = err?.response?.data;
      const messageFromServer =
        serverData && typeof serverData === 'object'
          ? serverData.message || serverData.error || ''
          : serverData || '';
      const errorMsg =
        messageFromServer || err?.message || err?.error || '';

      if (errorMsg.toLowerCase().includes('user not found')) {
        setError({ message: translate("auth.login.userNotFound") });
      } else if (
        errorMsg.toLowerCase().includes('invalid password') ||
        errorMsg.toLowerCase().includes('incorrect password')
      ) {
        setError({ message: translate("auth.login.invalidPassword") });
      } else if (errorMsg.toLowerCase().includes('verify your email')) {
        setError({ message: translate("auth.login.verifyEmailFirst") || errorMsg });
      } else {
        setError({ message: errorMsg || translate("auth.login.failedToLogin") || 'Login failed. Please try again.' });
      }
    }
    finally {
      setLoading(false);
    }
  };

  const handleSwitchType = () => {
    if (error?.actualType && onSelectUserType) {
      onSelectUserType(error.actualType);
      setError(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 w-full">
      <div className="text-center">
        <h2 className="text-xl font-bold text-white mb-1">{translate("auth.login.enterCredentials")}</h2>
        <p className="text-sm text-gray-300">
          {translate("auth.login.signInAs").replace("{userType}", userType)}
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error.message}
          {error.linkText && (
            <button
              type="button"
              onClick={handleSwitchType}
              className="text-primary font-bold hover:underline ml-1 mr-1"
            >
              {error.linkText}
            </button>
          )}
        </div>
      )}

      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
          {translate("auth.login.emailLabel")}
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={translate("auth.login.emailPlaceholder")}
          className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
          {translate("auth.login.passwordLabel")}
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={translate("auth.login.passwordPlaceholder")}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className={`absolute ${language === 'ar' ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700`}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
      </div>

      {/* Remember Me & Forgot Password */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 text-primary focus:ring-blue-400"
          />
          <span className="text-sm text-gray-300">{translate("auth.login.rememberMe")}</span>
        </label>
        <Link
          to="/forgot-password"
          className="text-sm text-primary hover:underline font-medium"
        >
          {translate("auth.login.forgotPassword")}
        </Link>

      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-primary hover:bg-primary-dark disabled:bg-gray-400 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
      >
        {loading ? translate("auth.login.signingIn") : translate("auth.login.signIn")}
      </button>

      {/* Back Button */}
      <button
        type="button"
        onClick={onBack}
        className="w-full border border-gray-600 text-white font-medium py-3 rounded-lg hover:bg-bg-medium transition-colors"
      >
        {translate("auth.login.back")}
      </button>
    </form>
  );
}
